import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import api from '../utils/api';
import config from '../config';

const StripeContext = createContext(null);

export const useStripe = () => {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error('useStripe must be used within a StripeProvider');
  }
  return context;
};

export const StripeProvider = ({ children }) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const tenant = useSelector(state => state.tenant);
  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);

  useEffect(() => {
    // Skip if already loaded
    if (stripePromise) {
      return;
    }

    const loadStripeKey = async () => {
      try {
        setLoading(true);
        setError(null);

        const { stripe_key } = await api.stripe.getKey();
        localStorage.setItem('stripePublicKey', stripe_key.public);
        setStripePromise(loadStripe(stripe_key.public));
      } catch (err) {
        console.error('Error loading Stripe key:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    const cachedKey = localStorage.getItem('stripePublicKey');
    const hasValidKey =
      cachedKey && cachedKey !== 'null' && cachedKey !== 'undefined';

    // Clean up invalid cached key
    if (cachedKey && !hasValidKey) {
      localStorage.removeItem('stripePublicKey');
    }

    // Only load if user is authenticated and tenant requires payments
    if (!hasValidKey && tenant && tenant.usePayments && isAuthenticated) {
      loadStripeKey();
    } else if (hasValidKey) {
      // Use cached key if available
      setStripePromise(loadStripe(cachedKey));
      setLoading(false);
    } else {
      // No key and not authenticated or no tenant - just stop loading
      setLoading(false);
    }
  }, [tenant, isAuthenticated]);

  // Method to clear Stripe key (useful for logout)
  const clearStripeKey = () => {
    localStorage.removeItem('stripePublicKey');
    setStripePromise(null);
  };

  const value = {
    stripePromise,
    loading,
    error,
    clearStripeKey
  };

  return (
    <StripeContext.Provider value={value}>{children}</StripeContext.Provider>
  );
};
